import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GARDIENS, QUIZ, quizResult, getGardien } from "@/data/gardiens";
import { GardienAvatar, GardienBadge } from "@/components/GardienBadge";
import { Loader2, RotateCcw, Sparkles, Printer } from "lucide-react";
import api from "@/lib/api";

function QuizStep({ step, onAnswer }) {
  const item = QUIZ[step];
  return (
    <div data-testid="gardien-quiz-step">
      <div className="flex gap-2 mb-6">
        {QUIZ.map((_, i) => <span key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-warm" : "bg-muted"}`} />)}
      </div>
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Question {step + 1} sur {QUIZ.length}</p>
      <h3 className="font-serif-display text-2xl sm:text-3xl mt-2 leading-snug" data-testid="gardien-quiz-question">{item.q}</h3>
      <div className="mt-6 space-y-3">
        {item.options.map((o, i) => (
          <button key={o.label} onClick={() => onAnswer(o.points)} data-testid={`gardien-quiz-answer-${i}`}
            className="w-full text-left bg-card rounded-2xl border border-warm/30 px-6 py-4 text-lg hover:bg-warm/10 hover:border-warm/60 transition-colors">
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function QuizResult({ g, onChoose, onRestart, saving }) {
  return (
    <div className="text-center" data-testid="gardien-quiz-result">
      <p className="text-sm uppercase tracking-[0.25em] text-warm">Son Gardien est…</p>
      <div className="flex justify-center mt-5"><GardienAvatar id={g.id} className="w-36 h-36 border-4 gentle-float" familiar={false} /></div>
      <h3 className="font-serif-display text-4xl mt-5" data-testid="gardien-quiz-result-name">{g.name}</h3>
      <p className="text-muted-foreground">{g.title}</p>
      <p className="mt-2 text-xs uppercase tracking-[0.25em] font-semibold" style={{ color: g.color }} data-testid="gardien-quiz-result-value">{g.value}</p>
      <p className="font-serif-display italic text-xl text-foreground/80 mt-3">«&nbsp;{g.quote}&nbsp;»</p>
      {g.story && <p className="text-foreground/70 mt-4 max-w-md mx-auto leading-relaxed">{g.story}</p>}
      {g.familiar && (
        <div className="mt-6 inline-flex items-center gap-4 bg-warm/10 border border-warm/30 rounded-2xl px-5 py-3 text-left" data-testid="gardien-quiz-result-familiar">
          <img src={g.familiar.image} alt={g.familiar.name} className="w-14 h-14 rounded-full object-cover" style={{ boxShadow: `0 0 0 3px ${g.color}44` }} />
          <span className="text-sm text-foreground/80 leading-snug">…accompagné de son fidèle compagnon<br /><b className="font-semibold text-foreground">{g.familiar.name}</b>, {g.familiar.title}</span>
        </div>
      )}
      <ul className="mt-6 text-left max-w-md mx-auto space-y-2" data-testid="gardien-quiz-result-activities">
        {g.activities.map((a) => (
          <li key={a} className="flex gap-3 text-foreground/80"><span className="mt-2 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} />{a}</li>
        ))}
      </ul>
      <div className="mt-7 flex flex-wrap justify-center gap-3">
        <button onClick={() => onChoose(g.id)} disabled={saving} data-testid="gardien-quiz-choose"
          className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 h-12 font-semibold hover:scale-105 transition-transform disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Choisir {g.name}
        </button>
        <button onClick={onRestart} data-testid="gardien-quiz-restart"
          className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-7 h-12 hover:bg-accent transition-colors">
          <RotateCcw className="w-4 h-4" /> Refaire le quiz
        </button>
      </div>
    </div>
  );
}

function Quiz({ onChoose, saving }) {
  const [step, setStep] = useState(0);
  const [scores, setScores] = useState({});
  const answer = (pts) => {
    const next = { ...scores };
    Object.entries(pts).forEach(([k, v]) => { next[k] = (next[k] || 0) + v; });
    setScores(next);
    setStep(step + 1);
  };
  if (step >= QUIZ.length) {
    return <QuizResult g={quizResult(scores)} onChoose={onChoose} saving={saving} onRestart={() => { setStep(0); setScores({}); }} />;
  }
  return (
    <>
      {step === 0 && (
        <>
          <p className="text-foreground/70 mb-4">Pensez à la personne que vous accompagnez, et répondez comme elle le ferait.</p>
          <GardienBadge id="agathe" eyebrow="Gardienne de ce quiz" className="mb-6" testId="gardien-quiz-host" />
        </>
      )}
      <QuizStep step={step} onAnswer={answer} />
    </>
  );
}

function DirectChoice({ current, onChoose, saving }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-4" data-testid="gardien-direct-grid">
      {GARDIENS.map((g) => (
        <button key={g.id} onClick={() => onChoose(g.id)} disabled={saving} data-testid={`gardien-choice-${g.id}`}
          className={`flex flex-col items-center gap-2 rounded-2xl p-3 transition-colors ${current === g.id ? "bg-warm/15 ring-2 ring-warm" : "hover:bg-accent/50"}`}>
          <GardienAvatar id={g.id} className="w-16 h-16 sm:w-20 sm:h-20" />
          <span className="font-serif-display text-base leading-tight text-center">{g.name}</span>
          <span className="text-[11px] uppercase tracking-[0.15em] text-center" style={{ color: g.color }}>{g.value.split(" - ")[0]}</span>
        </button>
      ))}
    </div>
  );
}

export function GardienPickerDialog({ open, onOpenChange, personName, current, onSave }) {
  const [mode, setMode] = useState("quiz");
  const [saving, setSaving] = useState(false);
  const choose = async (gid) => {
    setSaving(true);
    try {
      await onSave(gid);
      toast.success(`${getGardien(gid).name} accompagnera désormais ${personName}.`);
      onOpenChange(false);
    } catch { toast.error("Le Gardien n'a pas pu être enregistré."); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-background" data-testid="gardien-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display italic font-light text-3xl">Trouver le Gardien de {personName}</DialogTitle>
          <DialogDescription>Pensez à {personName} et répondez comme il ou elle le ferait.</DialogDescription>
        </DialogHeader>
        {current && <GardienBadge id={current} eyebrow="Gardien actuel" testId="gardien-picker-current" />}
        <div className="flex flex-wrap gap-2 my-2">
          {[["quiz", "Le quiz en 5 questions"], ["direct", "Choisir directement"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} data-testid={`gardien-picker-mode-${k}`}
              className={`px-5 min-h-10 py-2 rounded-full text-sm sm:text-base font-semibold transition-colors ${mode === k ? "bg-primary text-primary-foreground" : "border border-foreground/15 hover:bg-accent"}`}>
              {l}
            </button>
          ))}
        </div>
        {mode === "quiz" ? <Quiz onChoose={choose} saving={saving} /> : <DirectChoice current={current} onChoose={choose} saving={saving} />}
      </DialogContent>
    </Dialog>
  );
}

export function GardienPickerButton({ personName, current, onSave, testId, className = "" }) {
  const [open, setOpen] = useState(false);
  const g = current ? getGardien(current) : null;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={testId}
        className={`inline-flex items-center gap-2 rounded-full border border-warm/40 bg-warm/10 pl-1 pr-4 h-10 text-sm font-semibold text-foreground/80 hover:bg-warm/20 transition-colors ${className}`}>
        {g ? <GardienAvatar id={g.id} className="w-8 h-8" familiar={false} /> : <span className="w-8 h-8 rounded-full bg-warm/30 flex items-center justify-center"><Sparkles className="w-4 h-4 text-amber-700" /></span>}
        {g ? `Gardien : ${g.name}` : "Trouver son Gardien"}
      </button>
      <GardienPickerDialog open={open} onOpenChange={setOpen} personName={personName} current={current} onSave={onSave} />
    </>
  );
}

export function GardienCardButton({ url, fileName, testId, className = "" }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(url, { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = href; a.download = fileName; a.click();
      setTimeout(() => URL.revokeObjectURL(href), 1000);
      toast.success("Carte du Gardien téléchargée");
    } catch { toast.error("La carte n'a pas pu être créée."); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={download} disabled={busy} data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-full bg-warm text-warm-foreground px-5 h-10 text-sm font-semibold hover:scale-105 transition-transform disabled:opacity-60 ${className}`}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Carte à imprimer
    </button>
  );
}

export function GardienActivities({ id, personName }) {
  const g = getGardien(id);
  return (
    <div className="rounded-3xl bg-warm/10 border border-warm/25 p-6 sm:p-7" data-testid="gardien-activities">
      <div className="flex items-center gap-4 mb-5">
        <GardienAvatar id={g.id} className="w-16 h-16" />
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Activités conseillées par {g.name}</p>
          <p className="font-serif-display text-xl">Pour accompagner {personName} au quotidien</p>
        </div>
      </div>
      <ul className="space-y-3">
        {g.activities.map((a, i) => (
          <li key={a} className="flex gap-3 text-foreground/80 leading-relaxed" data-testid={`gardien-activity-${i}`}>
            <span className="mt-2 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: g.color }} /> {a}
          </li>
        ))}
      </ul>
    </div>
  );
}
